<template>
  <v-dialog
    v-model="dialog"
    max-width="630px"
    class="highestZIndex"
    :hide-overlay="hideOverlay"
    :persistent="persistent"
  >
    <v-card class="pa-5">
      <v-form v-model="loginFormValid" @submit.prevent="login">
        <v-text-field
          type="text"
          v-model="email"
          class="loginTextField"
          prepend-inner-icon="mdi-account"
          :rules="emailRules"
          :placeholder="!emailAutofilled ? ' ' : ''"
          v-on:keyup="checkForEnterKey"
        ></v-text-field>
        <v-text-field
          type="password"
          v-model="password"
          class="loginTextField"
          prepend-inner-icon="mdi-key-variant"
          :rules="passwordRules"
          :placeholder="!passwordAutofilled ? ' ' : ''"
          v-on:keyup="checkForEnterKey"
        ></v-text-field>
      </v-form>
      <span v-if="loginError" class="red--text">{{ loginError }}</span>
      <v-btn
        block
        :large="$vuetify.breakpoint.smAndUp"
        :disabled="!loginFormValid"
        :loading="isLoggingIn"
        color="secondary"
        @click="login"
        class="mt-2"
      >
        Log in
      </v-btn>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'LoginDialog',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    // When true, render without the dark scrim (used by the poll-results
    // login gate so the page background stays uniform).
    hideOverlay: {
      type: Boolean,
      default: false
    },
    // When true, the dialog can't be dismissed by click-outside/Esc — used by
    // the poll-results login gate so it can't be closed into an empty page.
    persistent: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      email: '',
      password: '',
      loginFormValid: false,
      isLoggingIn: false,
      emailAutofilled: false,
      passwordAutofilled: false,
      emailRules: [
        (value) => !!value || "Required.",
        (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail.";
        },
      ],
      passwordRules: [(value) => !!value || "Required."],
    };
  },
  
  computed: {
    ...mapGetters({
      loginError: 'auth/loginError',
      isLoggedIn: 'auth/isLoggedIn',
      playlistID: 'playlist/playlistID',
    }),
    
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },
  
  watch: {
    isLoggedIn(newValue) {
      if (newValue) {
        this.dialog = false;
        this.showWelcomeMessage();
      }
    }
  },
  
  methods: {
    ...mapActions({
      loginAction: 'auth/login',
      loadMasterPlaylist: 'playlist/loadMasterPlaylist',
      showSnackbar: 'ui/showSnackbar',
    }),
    
    async login() {
      if (!this.loginFormValid) return;
      
      this.isLoggingIn = true;
      
      try {
        await this.loginAction({
          email: this.email,
          password: this.password,
        });
        
        if (this.isLoggedIn && !this.playlistID) {
          await this.loadMasterPlaylist();
        }
      } catch (error) {
        console.error('Login failed:', error);
      } finally {
        this.isLoggingIn = false;
      }
    },
    
    checkForEnterKey(e) {
      if (e.keyCode === 13 && this.email !== '' && this.password !== '') {
        this.login();
      }
    },
    
    showWelcomeMessage() {
      this.showSnackbar('Welcome!');
    },
    
    resetForm() {
      this.email = '';
      this.password = '';
      this.loginFormValid = false;
    }
  }
};
</script>

<style scoped>
.highestZIndex {
  z-index: 999999999 !important;
}
</style>